import { useState } from "react";
import { Icon, Avatar, Modal, Field, Inp, Sel, Txt, SaveBtn, inputStyle } from "../shared/ui.jsx";
import { INV_PLACES, INV_CATS } from "../shared/constants.js";
import { today } from "../shared/utils";

export function InventoryForm({ onSave, onClose, existing, user }) {
  const [form, setForm] = useState(existing || {
    name: "", place: "Main house", area: "", spot: "",
    qty: "", condition: "", category: "", notes: "", changedDate: today(),
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = () => {
    if (!form.name.trim()) return;
    onSave({ ...form, id: existing?.id || Date.now(), updatedAt: new Date().toISOString(), updatedBy: user });
    onClose();
  };
  return (
    <>
      <Field label="Item name"><Inp value={form.name} onChange={e => set("name", e.target.value)} placeholder="Solar charger, Spare drill bits…" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Place">
          <Sel value={form.place} onChange={e => set("place", e.target.value)}>
            {INV_PLACES.map(p => <option key={p}>{p}</option>)}
          </Sel>
        </Field>
        <Field label="Category">
          <Sel value={form.category} onChange={e => set("category", e.target.value)}>
            <option value="">—</option>
            {INV_CATS.map(c => <option key={c}>{c}</option>)}
          </Sel>
        </Field>
      </div>
      <Field label="Area (room/zone)"><Inp value={form.area} onChange={e => set("area", e.target.value)} placeholder="Workshop, Kitchen, Shed…" /></Field>
      <Field label="Exact spot"><Inp value={form.spot} onChange={e => set("spot", e.target.value)} placeholder="Blue box under shelf, Top drawer…" /></Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
        <Field label="Quantity"><Inp value={form.qty} onChange={e => set("qty", e.target.value)} placeholder="2" /></Field>
        <Field label="Condition">
          <Sel value={form.condition} onChange={e => set("condition", e.target.value)}>
            <option value="">—</option>
            {["New", "Good", "Fair", "Poor", "Broken"].map(c => <option key={c}>{c}</option>)}
          </Sel>
        </Field>
      </div>
      <Field label="Notes"><Txt value={form.notes} onChange={e => set("notes", e.target.value)} placeholder="Works only with USB-C adapter…" /></Field>
      <Field label="Last updated"><Inp type="date" value={form.changedDate} onChange={e => set("changedDate", e.target.value)} /></Field>
      <SaveBtn onClick={save} />
    </>
  );
}

function InventoryCard({ item, onDelete, onEdit }) {
  return (
    <div style={{ padding: "0.85rem 1rem", background: "var(--paper-dark)", borderRadius: 4, marginBottom: "0.6rem", border: "1px solid var(--ink10)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: "1rem", color: "var(--ink)" }}>{item.name}</div>
          <div style={{ fontSize: "0.78rem", color: "var(--accent)", marginTop: 2 }}>
            📍 {[item.place, item.area, item.spot].filter(Boolean).join(" → ")}
          </div>
          <div style={{ fontSize: "0.75rem", color: "var(--ink50)", marginTop: 3 }}>
            {[item.category, item.qty && `Qty: ${item.qty}`, item.condition].filter(Boolean).join(" · ")}
          </div>
          {item.notes && <div style={{ fontSize: "0.8rem", fontStyle: "italic", color: "var(--ink70)", marginTop: 4 }}>{item.notes}</div>}
          {item.updatedBy && (
            <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 5 }}>
              <Avatar user={item.updatedBy} size={14} />
              <span style={{ fontSize: "0.67rem", color: "var(--ink30)" }}>by {item.updatedBy}</span>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
          <button onClick={() => onEdit(item)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink30)", padding: 4 }}><Icon name="edit" size={14} /></button>
          <button onClick={() => onDelete(item.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ink30)", padding: 4 }}><Icon name="trash" size={14} /></button>
        </div>
      </div>
    </div>
  );
}

export default function InventoryTab({ user, inventory, setInventory, searchQ }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterPlace, setFilterPlace] = useState("");
  const [filterCat, setFilterCat] = useState("");

  let items = inventory;
  if (searchQ) items = items.filter(i =>
    i.name.toLowerCase().includes(searchQ) ||
    (i.notes || "").toLowerCase().includes(searchQ) ||
    (i.category || "").toLowerCase().includes(searchQ) ||
    (i.spot || "").toLowerCase().includes(searchQ)
  );
  if (filterPlace) items = items.filter(i => i.place === filterPlace);
  if (filterCat) items = items.filter(i => i.category === filterCat);

  const save = (item) => {
    if (editItem) setInventory(inventory.map(i => i.id === item.id ? item : i));
    else setInventory([...inventory, item]);
    setEditItem(null); setShowForm(false);
  };
  const del = (id) => setInventory(inventory.filter(i => i.id !== id));

  return (
    <div>
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <Sel value={filterPlace} onChange={e => setFilterPlace(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="">All places</option>
          {INV_PLACES.map(p => <option key={p}>{p}</option>)}
        </Sel>
        <Sel value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...inputStyle, width: "auto", fontSize: "0.8rem" }}>
          <option value="">All categories</option>
          {INV_CATS.map(c => <option key={c}>{c}</option>)}
        </Sel>
      </div>
      <div style={{ fontSize: "0.78rem", color: "var(--ink50)", marginBottom: "0.5rem" }}>{items.length} item(s) · shared by all</div>
      {items.length === 0 && <p style={{ color: "var(--ink30)", fontStyle: "italic", fontSize: "0.9rem" }}>No items found.</p>}
      {items.map(i => (
        <InventoryCard key={i.id} item={i} onDelete={del} onEdit={item => { setEditItem(item); setShowForm(true); }} />
      ))}
      {showForm && (
        <Modal title={editItem ? "Edit Item" : "New Item"} onClose={() => { setShowForm(false); setEditItem(null); }}>
          <InventoryForm user={user} onSave={save} onClose={() => { setShowForm(false); setEditItem(null); }} existing={editItem} />
        </Modal>
      )}
    </div>
  );
}